import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { Money } from '@nucleus-commerce/core';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';

export interface SetVariantPriceDialogProps {
  /** How many rows are being priced; zero keeps the dialog closed. */
  count: number;
  currency: string;
  onApply: (price: Money) => void;
  onClose: () => void;
}

/** One price for the selected rows, as a draft: the save bar writes it. */
export function SetVariantPriceDialog({
  count,
  currency,
  onApply,
  onClose,
}: SetVariantPriceDialogProps) {
  const { t } = useTranslation(['products', 'common']);
  const [price, setPrice] = useState<Money | null>(null);
  return (
    <Dialog
      open={count > 0}
      onOpenChange={(open) => {
        if (!open) {
          setPrice(null);
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('products:variants.priceDialog.title', { count })}</DialogTitle>
          <DialogDescription>{t('products:variants.priceDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="variants-bulk-price">{t('products:variants.columns.price')}</Label>
          <MoneyInput
            id="variants-bulk-price"
            currency={currency}
            value={price}
            onChange={setPrice}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setPrice(null);
              onClose();
            }}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!price}
            onClick={() => {
              if (!price) return;
              onApply(price);
              setPrice(null);
            }}
          >
            {t('products:variants.priceDialog.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
